'use client';

import { type LayoutNodeStyleMap, type LayoutNode } from 'shop-ui/types/basic';
import { ELEMENT_TYPE, FEATURE_FLAG_KEYS, UI_LINK_TYPE_ENUM } from 'shop-ui/constants';
import { getElementUlId } from 'app/shop-ui/tools/getElementUlId';
import { combineComponent } from 'shop-ui/tools/combineComponent';
import { GRID_LAYOUT_TYPE } from 'shop-ui/section/Grid/rules';
import i18n from '@shared/i18n';

export const componentName = 'Button';

const config: LayoutNode = {
    id: '',
    type: ELEMENT_TYPE.BUTTON,
    name: componentName,
    enable: true, // 通常要開啟
    next: null,
    deletable: true, // 通常要開啟
};

export enum OFFICIAL_BUTTON_STYLE_KEY {
    DEFAULT_WHITE = 'official-button-default-white',
    DEFAULT_BLACK = 'official-button-default-black',
    DEFAULT_GRADIENT = 'official-button-default-gradient',
    WHITE_LARGE_ROUND = 'official-button-white-outline-large-round',
    BLACK_LARGE_ROUND = 'official-button-black-outline-large-round',
    RECT = 'official-button-rect',
    CIRCLE = 'official-button-circle',
    DELETE = 'official-button-delete',
    DOWNLOAD = 'official-button-download',
    NEXT = 'official-button-next',
    ADD_TO_CART = 'official-button-add-to-cart',
}

const officialButtonContentMap = {
    [OFFICIAL_BUTTON_STYLE_KEY.DEFAULT_WHITE]: i18n.gettext('Button'),
    [OFFICIAL_BUTTON_STYLE_KEY.DEFAULT_BLACK]: i18n.gettext('Button'),
    [OFFICIAL_BUTTON_STYLE_KEY.DEFAULT_GRADIENT]: i18n.gettext('Subscribe'),
    [OFFICIAL_BUTTON_STYLE_KEY.WHITE_LARGE_ROUND]: i18n.gettext('Buy now'),
    [OFFICIAL_BUTTON_STYLE_KEY.BLACK_LARGE_ROUND]: i18n.gettext('Buy now'),
    [OFFICIAL_BUTTON_STYLE_KEY.RECT]: i18n.gettext('Add'),
    [OFFICIAL_BUTTON_STYLE_KEY.CIRCLE]: '',
    [OFFICIAL_BUTTON_STYLE_KEY.DELETE]: i18n.gettext('Delete'),
    [OFFICIAL_BUTTON_STYLE_KEY.DOWNLOAD]: i18n.gettext('Download'),
    [OFFICIAL_BUTTON_STYLE_KEY.NEXT]: i18n.gettext('Next'),
    [OFFICIAL_BUTTON_STYLE_KEY.ADD_TO_CART]: i18n.gettext('Add to cart'),
};

export const showCutList = [
    {
        key: OFFICIAL_BUTTON_STYLE_KEY.DEFAULT_WHITE,
        type: ELEMENT_TYPE.BUTTON,
        content: officialButtonContentMap[OFFICIAL_BUTTON_STYLE_KEY.DEFAULT_WHITE],
        version: 1,
    },
    {
        key: OFFICIAL_BUTTON_STYLE_KEY.DEFAULT_BLACK,
        type: ELEMENT_TYPE.BUTTON,
        content: officialButtonContentMap[OFFICIAL_BUTTON_STYLE_KEY.DEFAULT_BLACK],
        version: 1,
    },
    {
        key: OFFICIAL_BUTTON_STYLE_KEY.DEFAULT_GRADIENT,
        type: ELEMENT_TYPE.BUTTON,
        content: officialButtonContentMap[OFFICIAL_BUTTON_STYLE_KEY.DEFAULT_GRADIENT],
        version: 1,
    },
    {
        key: OFFICIAL_BUTTON_STYLE_KEY.WHITE_LARGE_ROUND,
        type: ELEMENT_TYPE.BUTTON,
        content: officialButtonContentMap[OFFICIAL_BUTTON_STYLE_KEY.WHITE_LARGE_ROUND],
        version: 1,
    },
    {
        key: OFFICIAL_BUTTON_STYLE_KEY.BLACK_LARGE_ROUND,
        type: ELEMENT_TYPE.BUTTON,
        content: officialButtonContentMap[OFFICIAL_BUTTON_STYLE_KEY.BLACK_LARGE_ROUND],
        version: 1,
    },
    {
        key: OFFICIAL_BUTTON_STYLE_KEY.RECT,
        type: ELEMENT_TYPE.BUTTON,
        content: officialButtonContentMap[OFFICIAL_BUTTON_STYLE_KEY.RECT],
        version: 1,
    },
    {
        key: OFFICIAL_BUTTON_STYLE_KEY.CIRCLE,
        type: ELEMENT_TYPE.BUTTON,
        content: '',
        version: 1,
    },
    {
        key: OFFICIAL_BUTTON_STYLE_KEY.DELETE,
        type: ELEMENT_TYPE.BUTTON,
        content: officialButtonContentMap[OFFICIAL_BUTTON_STYLE_KEY.DELETE],
        version: 1,
    },
    {
        key: OFFICIAL_BUTTON_STYLE_KEY.DOWNLOAD,
        type: ELEMENT_TYPE.BUTTON,
        content: officialButtonContentMap[OFFICIAL_BUTTON_STYLE_KEY.DOWNLOAD],
        version: 1,
    },
    {
        key: OFFICIAL_BUTTON_STYLE_KEY.NEXT,
        type: ELEMENT_TYPE.BUTTON,
        content: officialButtonContentMap[OFFICIAL_BUTTON_STYLE_KEY.NEXT],
        version: 1,
    },
    {
        key: OFFICIAL_BUTTON_STYLE_KEY.ADD_TO_CART,
        type: ELEMENT_TYPE.BUTTON,
        content: officialButtonContentMap[OFFICIAL_BUTTON_STYLE_KEY.ADD_TO_CART],
        version: 1,
    },
];

export const features = [
    // 可調整大小
    FEATURE_FLAG_KEYS.RESIZE_HANDLE,

    // 佈局
    FEATURE_FLAG_KEYS.BUTTON_ALIGNMENT,

    // 樣式
    FEATURE_FLAG_KEYS.BACKGROUND_TYPE,
    FEATURE_FLAG_KEYS.BACKGROUND_COLOR,
    FEATURE_FLAG_KEYS.BACKGROUND_COLOR_GRADIENT,
    FEATURE_FLAG_KEYS.BORDER_COLOR,
    FEATURE_FLAG_KEYS.BORDER_RADIUS,
    FEATURE_FLAG_KEYS.PADDING_X,
    FEATURE_FLAG_KEYS.PADDING_Y,

    // 按鈕文字樣式
    FEATURE_FLAG_KEYS.RICH_TEXT,
    FEATURE_FLAG_KEYS.FONT_FAMILY,
    FEATURE_FLAG_KEYS.FONT_WEIGHT,
    FEATURE_FLAG_KEYS.FONT_SIZE,
    FEATURE_FLAG_KEYS.FONT_SIZE_CUSTOM,
    FEATURE_FLAG_KEYS.FONT_COLOR,
    FEATURE_FLAG_KEYS.FONT_STYLE,
    FEATURE_FLAG_KEYS.TEXT_LETTER_SPACING,

    // 按鈕圖示
    FEATURE_FLAG_KEYS.BUTTON_ICON,
    FEATURE_FLAG_KEYS.BUTTON_ICON_TYPE,
    FEATURE_FLAG_KEYS.BUTTON_ICON_POSITION,

    // 按鈕連結
    FEATURE_FLAG_KEYS.UI_LINK_TYPE,
    FEATURE_FLAG_KEYS.UI_LINK,
    FEATURE_FLAG_KEYS.SHOULD_OPEN_NEW_WINDOW,

    // 操作 - 滑鼠懸浮
    FEATURE_FLAG_KEYS.ENABLE_HOVER_STYLE,
    FEATURE_FLAG_KEYS.HOVER_BACKGROUND_TYPE,
    FEATURE_FLAG_KEYS.HOVER_BACKGROUND_COLOR,
    FEATURE_FLAG_KEYS.HOVER_BACKGROUND_COLOR_GRADIENT,
    FEATURE_FLAG_KEYS.HOVER_BORDER_COLOR,
    FEATURE_FLAG_KEYS.HOVER_BORDER_RADIUS,
    FEATURE_FLAG_KEYS.HOVER_FONT_COLOR,
    FEATURE_FLAG_KEYS.HOVER_FONT_STYLE,

    // 操作 - 滑鼠點擊
    FEATURE_FLAG_KEYS.ENABLE_ACTIVE_STYLE,
    FEATURE_FLAG_KEYS.ACTIVE_BACKGROUND_TYPE,
    FEATURE_FLAG_KEYS.ACTIVE_BACKGROUND_COLOR,
    FEATURE_FLAG_KEYS.ACTIVE_BACKGROUND_COLOR_GRADIENT,
    FEATURE_FLAG_KEYS.ACTIVE_BORDER_COLOR,
    FEATURE_FLAG_KEYS.ACTIVE_BORDER_RADIUS,
    FEATURE_FLAG_KEYS.ACTIVE_FONT_COLOR,
    FEATURE_FLAG_KEYS.ACTIVE_FONT_STYLE,

    // 動畫
    FEATURE_FLAG_KEYS.ANIMATION,
];

const basicDefaultStyle = {
    [FEATURE_FLAG_KEYS.FONT_SIZE]: 'custom',
    [FEATURE_FLAG_KEYS.BUTTON_ALIGNMENT]: 'center',
    [FEATURE_FLAG_KEYS.PADDING_X]: '20px',
    [FEATURE_FLAG_KEYS.PADDING_Y]: '10px',
    [FEATURE_FLAG_KEYS.BUTTON_ICON_TYPE]: 'home',
    [FEATURE_FLAG_KEYS.FONT_SIZE_CUSTOM]: '12',
    [FEATURE_FLAG_KEYS.BUTTON_ICON_POSITION]: 'left',
    [FEATURE_FLAG_KEYS.UI_LINK]: '',
    [FEATURE_FLAG_KEYS.UI_LINK_TYPE]: UI_LINK_TYPE_ENUM.PAGE,
    [FEATURE_FLAG_KEYS.SHOULD_OPEN_NEW_WINDOW]: false,
    [FEATURE_FLAG_KEYS.BACKGROUND_COLOR_GRADIENT]: true,
    [FEATURE_FLAG_KEYS.HOVER_BACKGROUND_COLOR_GRADIENT]: true,
    [FEATURE_FLAG_KEYS.ACTIVE_BACKGROUND_COLOR_GRADIENT]: true,
};

export const defaultStyleMap = {
    [OFFICIAL_BUTTON_STYLE_KEY.DEFAULT_WHITE]: {
        ...basicDefaultStyle,
        [FEATURE_FLAG_KEYS.BACKGROUND_COLOR]: 'rgba(255, 255, 255, 1)',
        [FEATURE_FLAG_KEYS.BORDER_RADIUS]: '6px',
        [FEATURE_FLAG_KEYS.FONT_FAMILY]: 'Inter',
        [FEATURE_FLAG_KEYS.FONT_WEIGHT]: '600',
        [FEATURE_FLAG_KEYS.FONT_COLOR]: 'rgba(63, 63, 63, 1)',
        [FEATURE_FLAG_KEYS.TEXT_LETTER_SPACING]: '0px',
        [FEATURE_FLAG_KEYS.TEXT_LINE_HEIGHT]: '1.3',
        [FEATURE_FLAG_KEYS.HOVER_BACKGROUND_COLOR]: 'rgba(63, 63, 63, 1)',
        [FEATURE_FLAG_KEYS.HOVER_FONT_COLOR]: 'rgba(255, 255, 255, 1)',
        [FEATURE_FLAG_KEYS.ACTIVE_BACKGROUND_COLOR]: 'rgba(21, 136, 244, 1)',
        [FEATURE_FLAG_KEYS.ACTIVE_FONT_COLOR]: 'rgba(255, 255, 255, 1)',
    },
    [OFFICIAL_BUTTON_STYLE_KEY.DEFAULT_BLACK]: {
        ...basicDefaultStyle,
        [FEATURE_FLAG_KEYS.BACKGROUND_COLOR]: 'rgba(63, 63, 63, 1)',
        [FEATURE_FLAG_KEYS.BORDER_RADIUS]: '6px',
        [FEATURE_FLAG_KEYS.FONT_FAMILY]: 'Inter',
        [FEATURE_FLAG_KEYS.FONT_WEIGHT]: '600',
        [FEATURE_FLAG_KEYS.FONT_COLOR]: 'rgba(255, 255, 255, 1)',
        [FEATURE_FLAG_KEYS.TEXT_LETTER_SPACING]: '0px',
        [FEATURE_FLAG_KEYS.TEXT_LINE_HEIGHT]: '1.3',
        [FEATURE_FLAG_KEYS.HOVER_BACKGROUND_COLOR]: 'rgba(255, 255, 255, 1)',
        [FEATURE_FLAG_KEYS.HOVER_FONT_COLOR]: 'rgba(63, 63, 63, 1)',
        [FEATURE_FLAG_KEYS.ACTIVE_BACKGROUND_COLOR]: 'rgba(21, 136, 244, 1)',
        [FEATURE_FLAG_KEYS.ACTIVE_FONT_COLOR]: 'rgba(255, 255, 255, 1)',
    },
    [OFFICIAL_BUTTON_STYLE_KEY.DEFAULT_GRADIENT]: {
        ...basicDefaultStyle,
        [FEATURE_FLAG_KEYS.BACKGROUND_COLOR]:
            'linear-gradient(90deg, rgba(255, 226, 89, 1) 0%, rgba(255, 167, 81, 1) 100%)',
        [FEATURE_FLAG_KEYS.BORDER_RADIUS]: '30px',
        [FEATURE_FLAG_KEYS.FONT_FAMILY]: 'montserrat',
        [FEATURE_FLAG_KEYS.FONT_WEIGHT]: '500',
        [FEATURE_FLAG_KEYS.FONT_COLOR]: 'rgba(46, 46, 46, 1)',
        [FEATURE_FLAG_KEYS.TEXT_LETTER_SPACING]: '0px',
        [FEATURE_FLAG_KEYS.TEXT_LINE_HEIGHT]: '1.3',
        [FEATURE_FLAG_KEYS.HOVER_BACKGROUND_COLOR]:
            'linear-gradient(90deg, rgba(255, 226, 89, 1) 0%, rgba(255, 167, 81, 1) 100%)',
        [FEATURE_FLAG_KEYS.HOVER_FONT_COLOR]: 'rgba(255, 255, 255, 1)',
        [FEATURE_FLAG_KEYS.ACTIVE_BACKGROUND_COLOR]: 'rgba(255, 167, 81, 1)',
        [FEATURE_FLAG_KEYS.ACTIVE_FONT_COLOR]: 'rgba(255, 255, 255, 1)',
    },
    [OFFICIAL_BUTTON_STYLE_KEY.WHITE_LARGE_ROUND]: {
        ...basicDefaultStyle,
        [FEATURE_FLAG_KEYS.BACKGROUND_COLOR]: 'rgba(255, 255, 255, 1)',
        [FEATURE_FLAG_KEYS.BORDER_COLOR]: 'rgba(46, 46, 46, 1)',
        [FEATURE_FLAG_KEYS.BORDER_RADIUS]: '30px',
        [FEATURE_FLAG_KEYS.FONT_FAMILY]: 'montserrat',
        [FEATURE_FLAG_KEYS.FONT_WEIGHT]: '500',
        [FEATURE_FLAG_KEYS.FONT_COLOR]: 'rgba(46, 46, 46, 1)',
        [FEATURE_FLAG_KEYS.TEXT_LETTER_SPACING]: '0px',
        [FEATURE_FLAG_KEYS.TEXT_LINE_HEIGHT]: '1.3',
        [FEATURE_FLAG_KEYS.HOVER_BACKGROUND_COLOR]: 'rgba(1, 1, 1, 1)',
        [FEATURE_FLAG_KEYS.HOVER_FONT_COLOR]: 'rgba(255, 255, 255, 1)',
        [FEATURE_FLAG_KEYS.ACTIVE_BACKGROUND_COLOR]: 'rgba(127, 127, 127, 1)',
        [FEATURE_FLAG_KEYS.ACTIVE_FONT_COLOR]: 'rgba(255, 255, 255, 1)',
    },
    [OFFICIAL_BUTTON_STYLE_KEY.BLACK_LARGE_ROUND]: {
        ...basicDefaultStyle,
        [FEATURE_FLAG_KEYS.BACKGROUND_COLOR]: 'rgba(57, 57, 62, 1)',
        [FEATURE_FLAG_KEYS.BORDER_RADIUS]: '30px',
        [FEATURE_FLAG_KEYS.FONT_FAMILY]: 'montserrat',
        [FEATURE_FLAG_KEYS.FONT_WEIGHT]: '500',
        [FEATURE_FLAG_KEYS.FONT_COLOR]: 'rgba(255, 255, 255, 1)',
        [FEATURE_FLAG_KEYS.TEXT_LETTER_SPACING]: '0px',
        [FEATURE_FLAG_KEYS.TEXT_LINE_HEIGHT]: '1.3',
        [FEATURE_FLAG_KEYS.HOVER_BACKGROUND_COLOR]: 'rgba(129, 129, 129, 1)',
        [FEATURE_FLAG_KEYS.HOVER_FONT_COLOR]: 'rgba(255, 255, 255, 1)',
        [FEATURE_FLAG_KEYS.ACTIVE_BACKGROUND_COLOR]: 'rgba(21, 136, 244, 1)',
        [FEATURE_FLAG_KEYS.ACTIVE_FONT_COLOR]: 'rgba(255, 255, 255, 1)',
    },
    [OFFICIAL_BUTTON_STYLE_KEY.RECT]: {
        ...basicDefaultStyle,
        [FEATURE_FLAG_KEYS.BACKGROUND_COLOR]: 'rgba(173, 205, 240, 1)',
        [FEATURE_FLAG_KEYS.BORDER_RADIUS]: '0px',
        [FEATURE_FLAG_KEYS.FONT_FAMILY]: 'montserrat',
        [FEATURE_FLAG_KEYS.FONT_WEIGHT]: '500',
        [FEATURE_FLAG_KEYS.FONT_COLOR]: 'rgba(52, 51, 48, 1)',
        [FEATURE_FLAG_KEYS.TEXT_LETTER_SPACING]: '0px',
        [FEATURE_FLAG_KEYS.TEXT_LINE_HEIGHT]: '1.3',
        [FEATURE_FLAG_KEYS.BUTTON_ICON]: true,
        [FEATURE_FLAG_KEYS.BUTTON_ICON_TYPE]: 'fi-rr-plus',
        [FEATURE_FLAG_KEYS.HOVER_BACKGROUND_COLOR]: 'rgba(173, 205, 240, 1)',
        [FEATURE_FLAG_KEYS.HOVER_FONT_COLOR]: 'rgba(52, 51, 48, 1)',
        [FEATURE_FLAG_KEYS.ACTIVE_BACKGROUND_COLOR]: 'rgba(173, 205, 240, 1)',
        [FEATURE_FLAG_KEYS.ACTIVE_FONT_COLOR]: 'rgba(52, 51, 48, 1)',
    },
    [OFFICIAL_BUTTON_STYLE_KEY.CIRCLE]: {
        ...basicDefaultStyle,
        [FEATURE_FLAG_KEYS.BACKGROUND_COLOR]: 'rgba(211, 194, 255, 1)',
        [FEATURE_FLAG_KEYS.BORDER_RADIUS]: '50px',
        [FEATURE_FLAG_KEYS.FONT_FAMILY]: 'montserrat',
        [FEATURE_FLAG_KEYS.FONT_WEIGHT]: '500',
        [FEATURE_FLAG_KEYS.FONT_COLOR]: 'rgba(36, 0, 70, 1)',
        [FEATURE_FLAG_KEYS.TEXT_LETTER_SPACING]: '0px',
        [FEATURE_FLAG_KEYS.PADDING_X]: '24px',
        [FEATURE_FLAG_KEYS.TEXT_LINE_HEIGHT]: '1.3',
        [FEATURE_FLAG_KEYS.BUTTON_ICON]: true,
        [FEATURE_FLAG_KEYS.BUTTON_ICON_TYPE]: 'fi-rr-arrow-down',
        [FEATURE_FLAG_KEYS.HOVER_BACKGROUND_COLOR]: 'rgba(192, 168, 255, 1)',
        [FEATURE_FLAG_KEYS.ACTIVE_BACKGROUND_COLOR]: 'rgba(172, 140, 255, 1)',
    },
    [OFFICIAL_BUTTON_STYLE_KEY.DELETE]: {
        ...basicDefaultStyle,
        [FEATURE_FLAG_KEYS.BACKGROUND_COLOR]: 'rgba(255, 155, 84, 1)',
        [FEATURE_FLAG_KEYS.BORDER_RADIUS]: '30px',
        [FEATURE_FLAG_KEYS.FONT_FAMILY]: 'montserrat',
        [FEATURE_FLAG_KEYS.FONT_WEIGHT]: '500',
        [FEATURE_FLAG_KEYS.FONT_COLOR]: 'rgba(114, 19, 0, 1)',
        [FEATURE_FLAG_KEYS.TEXT_LETTER_SPACING]: '0px',
        [FEATURE_FLAG_KEYS.TEXT_LINE_HEIGHT]: '1.3',
        [FEATURE_FLAG_KEYS.FONT_SIZE_CUSTOM]: '18',
        [FEATURE_FLAG_KEYS.BUTTON_ICON]: true,
        [FEATURE_FLAG_KEYS.BUTTON_ICON_TYPE]: 'fi-rr-cross',
        [FEATURE_FLAG_KEYS.BUTTON_ICON_POSITION]: 'right',
        [FEATURE_FLAG_KEYS.HOVER_BACKGROUND_COLOR]: 'rgba(255, 156, 156, 1)',
        [FEATURE_FLAG_KEYS.ACTIVE_BACKGROUND_COLOR]: 'rgba(255, 156, 156, 1)',
    },
    [OFFICIAL_BUTTON_STYLE_KEY.DOWNLOAD]: {
        ...basicDefaultStyle,
        [FEATURE_FLAG_KEYS.BACKGROUND_COLOR]: 'rgba(255, 255, 255, 1)',
        [FEATURE_FLAG_KEYS.BORDER_RADIUS]: '0px',
        [FEATURE_FLAG_KEYS.FONT_FAMILY]: 'montserrat',
        [FEATURE_FLAG_KEYS.FONT_WEIGHT]: '600',
        [FEATURE_FLAG_KEYS.FONT_COLOR]: 'rgba(62, 144, 106, 1)',
        [FEATURE_FLAG_KEYS.TEXT_LETTER_SPACING]: '0px',
        [FEATURE_FLAG_KEYS.TEXT_LINE_HEIGHT]: '1.3',
        [FEATURE_FLAG_KEYS.BUTTON_ICON]: true,
        [FEATURE_FLAG_KEYS.BUTTON_ICON_TYPE]: 'fi-rr-cloud-download-alt',
        [FEATURE_FLAG_KEYS.BUTTON_ICON_POSITION]: 'right',
        [FEATURE_FLAG_KEYS.HOVER_BACKGROUND_COLOR]: 'rgba(141, 255, 113, 1)',
        [FEATURE_FLAG_KEYS.ACTIVE_BACKGROUND_COLOR]: 'rgba(86, 248, 115, 1)',
    },
    [OFFICIAL_BUTTON_STYLE_KEY.NEXT]: {
        ...basicDefaultStyle,
        [FEATURE_FLAG_KEYS.BACKGROUND_COLOR]: 'rgba(128, 237, 153, 1)',
        [FEATURE_FLAG_KEYS.BORDER_RADIUS]: '0px',
        [FEATURE_FLAG_KEYS.FONT_FAMILY]: 'montserrat',
        [FEATURE_FLAG_KEYS.FONT_WEIGHT]: '400',
        [FEATURE_FLAG_KEYS.FONT_COLOR]: 'rgba(8, 28, 21, 1)',
        [FEATURE_FLAG_KEYS.TEXT_LETTER_SPACING]: '0px',
        [FEATURE_FLAG_KEYS.TEXT_LINE_HEIGHT]: '1.3',
        [FEATURE_FLAG_KEYS.BUTTON_ICON]: true,
        [FEATURE_FLAG_KEYS.BUTTON_ICON_TYPE]: 'fi-rr-arrow-right',
        [FEATURE_FLAG_KEYS.BUTTON_ICON_POSITION]: 'right',
        [FEATURE_FLAG_KEYS.HOVER_BACKGROUND_COLOR]: 'rgba(90, 227, 121, 1)',
        [FEATURE_FLAG_KEYS.ACTIVE_BACKGROUND_COLOR]: 'rgba(73, 196, 101, 1)',
    },
    [OFFICIAL_BUTTON_STYLE_KEY.ADD_TO_CART]: {
        ...basicDefaultStyle,
        [FEATURE_FLAG_KEYS.BACKGROUND_COLOR]: 'rgba(255, 255, 255, 1)',
        [FEATURE_FLAG_KEYS.BORDER_RADIUS]: '0px',
        [FEATURE_FLAG_KEYS.FONT_FAMILY]: 'Inter',
        [FEATURE_FLAG_KEYS.FONT_WEIGHT]: '500',
        [FEATURE_FLAG_KEYS.FONT_COLOR]: 'rgba(63, 63, 63, 1)',
        [FEATURE_FLAG_KEYS.TEXT_LETTER_SPACING]: '0px',
        [FEATURE_FLAG_KEYS.TEXT_LINE_HEIGHT]: '1.3',
        [FEATURE_FLAG_KEYS.BUTTON_ICON]: true,
        [FEATURE_FLAG_KEYS.BUTTON_ICON_TYPE]: 'fi-rr-shopping-cart',
        [FEATURE_FLAG_KEYS.HOVER_BACKGROUND_COLOR]: 'rgba(236, 236, 236, 1)',
        [FEATURE_FLAG_KEYS.HOVER_FONT_COLOR]: 'rgba(255, 255, 255, 1)',
        [FEATURE_FLAG_KEYS.ACTIVE_BACKGROUND_COLOR]: 'rgba(21, 136, 244, 1)',
        [FEATURE_FLAG_KEYS.ACTIVE_FONT_COLOR]: 'rgba(255, 255, 255, 1)',
    },
} as const;

// 只是範例，請依照需求調整
export function createButton(
    name: string,
    content: string,
    presetStyle?: LayoutNodeStyleMap[string]
) {
    const id = getElementUlId();

    const layout = {
        ...config,
        id,
    };

    const defaultStyle = defaultStyleMap[name];

    return {
        id,
        layout,
        style: {
            ...defaultStyle,
            ...presetStyle,
        },
        content,
    };
}

export function generate(
    sectionName: string = i18n.gettext('區塊'),
    name: string = OFFICIAL_BUTTON_STYLE_KEY.DEFAULT_WHITE,
    content: string = '',
    options?: { shouldWrap?: boolean }
) {
    const button = createButton(name, content);

    const shouldWrap = options?.shouldWrap ?? true;

    return combineComponent(sectionName, [button], {
        primaryGridType: GRID_LAYOUT_TYPE.SINGLE_CELL,
        secondaryGridType: GRID_LAYOUT_TYPE.SINGLE_CELL,
        shouldWrap,
    });
}
